/**
 * One line of a keeper roster, and the only one — the phone list and the desktop table draw the
 * same component so the two cannot drift.
 *
 * It is presentational on purpose. Everything it shows was decided in `domain/keeperRow.ts` or by
 * the screen that owns the slate; this file arranges it and gets the hit areas right. The 76px
 * height and the 78px tag column are fixed on every row, occupied or not, because a column of
 * costs that shifts sideways depending on whether the man above could be franchised is a column
 * nobody can read down.
 */

import { ClubChip, PositionChip } from '../chips.js';
import { nflInfoOf } from '../NflChip.js';
import { Callout } from './Callout.js';
import { splitName, type StatusChip, type TagState } from '../../domain/keeperRow.js';

/*
 * POSITION HUES MOVED to `ui/chips.tsx`, with the club table, when the Calculator became the
 * fourth screen drawing the same two chips. Re-exported here because the pick summary and the
 * submit sheet import the table from this file, and a moved export that changes every import
 * site at once is a bigger diff than the thing it is tidying.
 */
export { POS_HUE } from '../chips.js';

export interface CostView {
  /**
   * The round he lands on, after any escalation. On a row nobody has chosen this is simply what
   * he would cost, and it is the only thing the row shows: an unchosen player is not charged to
   * a pick, so there is no slot code to name.
   */
  round: number;
  /** The round he would have cost, shown struck through when the two differ. */
  base: number;
  /** `4.08` once the order exists, or a round-only label before the lottery. */
  code: string;
  overall: number | null;
  tagged: boolean;
}

export interface DeclarationRowProps {
  player: string;
  pos?: string;
  chips: readonly StatusChip[];
  on: boolean;
  /** Locked rows are on a submitted slate, or mandatory. Visible, not interactive. */
  locked?: boolean;
  disabled?: boolean;
  tag: TagState | null;
  cost: CostView | null;
  escalationOpen?: boolean;
  onToggle?: () => void;
  onTag?: () => void;
  /**
   * Given where the caret should sit — the centre of the cost button, measured from the row's
   * left edge — so the escalation panel's notch lands on the control that opened it whatever the
   * row's width. Measured rather than assumed: the box is 70px minimum and grows with its code.
   */
  onCost?: (caret: string) => void;
  /** Rendered under the row: the escalation strip, a refusal, a commissioner's forfeit form. */
  children?: React.ReactNode;
}

export function DeclarationRow(props: DeclarationRowProps) {
  const { player, pos, chips, on, locked, disabled, tag, cost } = props;
  const { surname, given } = splitName(player);
  const club = nflInfoOf(player)?.team ?? null;

  return (
    <>
      <div className={`kdrow${on ? ' on' : ''}${locked ? ' locked' : ''}`}>
        {/*
          The BUTTON is 44px and the box inside it is 22px. Growing the box to meet the finger
          would change the row's shape; growing the button and giving the space back with a
          negative margin leaves the layout alone and still lands the tap.
        */}
        <button type="button" className={`kdcheck${on ? ' on' : ''}`} disabled={disabled || locked}
          role="checkbox" aria-checked={on} aria-label={`Keep ${player}`}
          onClick={props.onToggle}>
          <i aria-hidden="true">{on ? '✓' : ''}</i>
        </button>

        <span className="kdname">
          {/*
            Surname first and never wrapped: sixteen rows are scanned down the surnames, and a
            name that folds onto a second line breaks the column the eye is running down. The
            given name is what gives way, because it is the part you already know by then.
          */}
          <span className="kdnameline">
            <b className="kdsur">{surname}{given ? ',' : ''}</b>
            {given && <span className="kdgiven">{given}</span>}
          </span>
          {/*
            IDENTITY ONLY. Club and position say who he is and travel with the name — on a desktop
            row they sit on the same line as it. What he is (farm, dropped, sleeper) is a separate
            question with its own column at full width, so it is a separate element.
          */}
          <span className="kdchips">
            <ClubChip team={club} />
            <PositionChip pos={pos} />
          </span>
        </span>

        {/* Its own column from 900px up; on a phone it falls in beside the club and position. */}
        <span className="kdstatus">
          {chips.map((c) => (
            <Callout key={c.kind} text={c.callout} by={c.by} tone={`status ${c.kind}`}
              label={c.label} />
          ))}
        </span>

        {/* Always present, always 78px — see the note at the top of this file. */}
        <span className="kdtag">
          {on && tag && (
            tag.state === 'blocked'
              ? <Callout className="kdcantwrap" text={tag.reason} tone="" label={
                  <span className="kdcant">Can&rsquo;t Tag</span>
                } />
              : (
                <button type="button"
                  className={`kdtagbtn${tag.state === 'on' ? ' on' : ''}`}
                  aria-pressed={tag.state === 'on'} disabled={disabled || locked}
                  onClick={props.onTag}>
                  <span className="kdtaglab">
                    {tag.state === 'on' ? 'Franchised' : 'Franchise?'}
                  </span>
                  <span className="kdtrack"><span className="kdknob" /></span>
                </button>
              )
          )}
        </span>

        {/*
          Not chosen, but still priced. The design shows the cost on every row, chosen or not,
          because the whole decision is a comparison — you cannot weigh a 5th against an 11th if
          only the ones you have already ticked say what they cost. It is a plain label, not the
          box: there is no pick to escalate until he is on the slate, so there is nothing to tap.
        */}
        {!on && cost && (
          <span className="kdcost ghost">Rd {cost.round}</span>
        )}

        {on && cost && (
          <button type="button"
            className={`kdcost${cost.tagged ? ' tagged' : ''}${cost.round !== cost.base ? ' moved' : ''}`}
            aria-expanded={props.escalationOpen ?? false} disabled={disabled || locked}
            aria-label={`${player} costs ${cost.code}. Change which pick pays.`}
            onClick={(e) => {
              /*
                MEASURED WITH RECTS, not offsets.
                `offsetLeft` is relative to the nearest POSITIONED ancestor, and `.kdrow` is
                `position: relative` — so the button's offset was already row-relative and
                subtracting the row's own offset took the same distance off twice. The notch
                landed a column and a half to the left of the box that opened it.
              */
              const btn = e.currentTarget.getBoundingClientRect();
              const row = e.currentTarget.closest('.kdrow')?.getBoundingClientRect();
              const mid = row ? btn.left - row.left + btn.width / 2 : btn.width / 2;
              props.onCost?.(`${Math.round(mid)}px`);
            }}>
            {/*
              THE LIVE FIGURE FIRST, ALWAYS.
              The struck price used to lead, so a moved keeper's real cost started forty pixels
              further right than an unmoved one's and the column could not be read down. What is
              being paid is the same shape on every row now; what it replaced trails it.
            */}
            <span className="pick">{cost.code}</span>
            {cost.overall !== null && <span className="over">#{cost.overall}</span>}
            {cost.round !== cost.base && <span className="struck">Rd {cost.base}</span>}
            <span className="caret" aria-hidden="true" />
          </button>
        )}
      </div>
      {/* The strip inherits the row's own wash so its notch can be the row bleeding down. */}
      <div className={`kdescslot${on ? ' on' : ''}${locked ? ' locked' : ''}`}>
        {props.children}
      </div>
    </>
  );
}
